/**
 * SF-T-83: links to things on a task source that an agent may ask the hub to read (`sf-agent tasks source <url>`):
 * an Asana task or person, a Trello card, a GitHub issue. Anything else is refused. The hub then checks the thing
 * is on the project's own source (services/sources/lookup.ts); this file only says what a link points at.
 */
export type SourceLink =
  | { kind: 'asana'; type: 'task'; id: string }
  | { kind: 'asana'; type: 'person'; id: string }
  | { kind: 'trello'; type: 'card'; id: string }
  | { kind: 'github'; type: 'issue'; repo: string; number: number };

/** What each kind of link can point at, for the refusal's message. */
export const SOURCE_LINK_KINDS = 'an Asana task or person, a Trello card (trello.com/c/…) or a GitHub issue (github.com/<owner>/<repo>/issues/<n>)';

const ASANA_ID = /^\d{1,30}$/;

function hostOf(url: URL): string {
  return url.hostname.toLowerCase().replace(/^www\./, '');
}

/** The path's parts, without empty ones (a trailing slash). */
function partsOf(url: URL): string[] {
  return url.pathname.split('/').filter(Boolean);
}

/**
 * Asana's two link forms:
 * - old: `/0/<project>/<task>` (with `/f` for the focused view), `/0/profile/<person>`;
 * - new: `/1/<workspace>/…/task/<task>` (in a project, an inbox, a search…), `/1/<workspace>/profile/<person>`.
 * A comment's link (`…/task/<task>/comment/<story>`) is its task's.
 */
function asanaLink(parts: string[]): SourceLink | null {
  if (parts[parts.length - 1] === 'f') parts = parts.slice(0, -1);
  if (parts[0] === '0') {
    if (parts.length === 3 && parts[1] === 'profile' && ASANA_ID.test(parts[2])) return { kind: 'asana', type: 'person', id: parts[2] };
    if (parts.length === 3 && ASANA_ID.test(parts[1]) && ASANA_ID.test(parts[2])) return { kind: 'asana', type: 'task', id: parts[2] };
    return null;
  }
  if (parts[0] === '1' && ASANA_ID.test(parts[1] ?? '')) {
    const rest = parts.slice(2);
    if (rest.length === 2 && rest[0] === 'profile' && ASANA_ID.test(rest[1])) return { kind: 'asana', type: 'person', id: rest[1] };
    const at = rest.lastIndexOf('task');
    const id = at >= 0 ? rest[at + 1] : undefined;
    if (!id || !ASANA_ID.test(id)) return null;
    const after = rest.slice(at + 2);
    if (after.length && !(after.length === 2 && after[0] === 'comment' && ASANA_ID.test(after[1]))) return null;
    return { kind: 'asana', type: 'task', id };
  }
  return null;
}

/** `trello.com/c/<shortLink>[/<n>-<slug>]`. */
function trelloLink(parts: string[]): SourceLink | null {
  if (parts[0] !== 'c' || parts.length < 2 || parts.length > 3) return null;
  return /^[A-Za-z0-9]{8,24}$/.test(parts[1]) ? { kind: 'trello', type: 'card', id: parts[1] } : null;
}

/** `github.com/<owner>/<repo>/issues/<n>` (a comment's anchor or query ignored). Pull requests aren't issues here. */
function githubLink(parts: string[]): SourceLink | null {
  if (parts.length !== 4 || parts[2] !== 'issues') return null;
  const [owner, repo, , n] = parts;
  if (!/^[A-Za-z0-9-]{1,39}$/.test(owner) || !/^[A-Za-z0-9._-]{1,100}$/.test(repo) || !/^\d{1,9}$/.test(n)) return null;
  const number = Number(n);
  return number >= 1 ? { kind: 'github', type: 'issue', repo: `${owner}/${repo}`, number } : null;
}

/** What a link points at, or null when it isn't one an agent may have read (another site, a board, a pull request). */
export function parseSourceLink(value: string): SourceLink | null {
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (url.username || url.password || url.port) return null;
  const host = hostOf(url);
  const parts = partsOf(url);
  if (host === 'app.asana.com') return asanaLink(parts);
  if (host === 'trello.com') return trelloLink(parts);
  if (host === 'github.com') return githubLink(parts);
  return null;
}

/** "Asana task 1209…", "Trello card AbCd1234", "GitHub issue owner/repo#12": how a link is named in messages and the cache. */
export function sourceLinkLabel(link: SourceLink): string {
  switch (link.type) {
    case 'task':
      return `Asana task ${link.id}`;
    case 'person':
      return `Asana person ${link.id}`;
    case 'card':
      return `Trello card ${link.id}`;
    case 'issue':
      return `GitHub issue ${link.repo}#${link.number}`;
  }
}
